import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSlug from 'rehype-slug'

import mdOverview from '../platform/Overview.md'
import mdGettingStartedTutorial from '../platform/tutorials/GettingStarted.md'
import mdConfigurationReference from '../platform/reference/Configuration.md'
import mdShortcutsReference from '../platform/reference/ShortcutsAndHiddenFeatures.md'
import mdSubmissionsTutorial from '../platform/tutorials/Submissions.md'
import mdSubmissionsReference from '../platform/reference/Submissions.md'
import mdReadingResultsTutorial from '../platform/tutorials/ReadingResults.md'
import mdPlottingResultsTutorial from '../platform/tutorials/PlottingResults.md'
import mdReviewingImagesTutorial from '../platform/tutorials/ReviewingImages.md'
import mdRunMediaAndReportsTutorial from '../platform/tutorials/RunMediaAndReports.md'
import mdDashboardReference from '../platform/reference/Dashboard.md'
import mdRunMediaAndReportsReference from '../platform/reference/RunMediaAndReports.md'
import mdComplianceReportingReference from '../platform/reference/ComplianceReporting.md'
import mdOpeningResultsInMAVTutorial from '../platform/tutorials/OpeningResultsInMAV.md'
import mdMAVIntegrationReference from '../platform/reference/MAVIntegration.md'
import mdManagingYourAccountTutorial from '../platform/tutorials/ManagingYourAccount.md'

// ── Types ──
interface DocNode {
  id: string
  title: string
  /** Omitted on section nodes, which only group their children. */
  mdUrl?: string
  children?: DocNode[]
}

/**
 * Register every platform doc here. Nested `children` render as a collapsible
 * group. `id` is the value used by the `?doc=` query parameter and by
 * cross-document links, so it must stay stable once published.
 */
const DOC_TREE: DocNode[] = [
  { id: 'overview', title: 'Overview', mdUrl: mdOverview },
  {
    id: 'getting-started',
    title: 'Setting Up a Car, Project, and Run',
    mdUrl: mdGettingStartedTutorial
  },
  { id: 'configuration', title: 'Configuration Reference', mdUrl: mdConfigurationReference },
  {
    id: 'shortcuts-hidden-features',
    title: 'Shortcuts and Hidden Features',
    mdUrl: mdShortcutsReference
  },
  {
    id: 'submissions',
    title: 'Submissions',
    children: [
      { id: 'submissions-tutorial', title: 'Submitting a Run', mdUrl: mdSubmissionsTutorial },
      { id: 'submissions-reference', title: 'Submissions Reference', mdUrl: mdSubmissionsReference }
    ]
  },
  {
    id: 'results',
    title: 'Results and Dashboard',
    children: [
      { id: 'results-tutorial', title: 'Reading Headline Data', mdUrl: mdReadingResultsTutorial },
      {
        id: 'plotting-results-tutorial',
        title: 'Plotting Headline Data',
        mdUrl: mdPlottingResultsTutorial
      },
      {
        id: 'reviewing-images-tutorial',
        title: 'Reviewing Post-Processing Images',
        mdUrl: mdReviewingImagesTutorial
      },
      {
        id: 'run-media-reports-tutorial',
        title: 'Adding Media and Generating a Report',
        mdUrl: mdRunMediaAndReportsTutorial
      },
      { id: 'dashboard-reference', title: 'Dashboard Reference', mdUrl: mdDashboardReference },
      {
        id: 'compliance-reporting-reference',
        title: 'Compliance Reporting Reference',
        mdUrl: mdComplianceReportingReference
      },
      {
        id: 'run-media-reports-reference',
        title: 'Run Media and Report Generator Reference',
        mdUrl: mdRunMediaAndReportsReference
      }
    ]
  },
  {
    id: 'visualization',
    title: 'Visualization',
    children: [
      {
        id: 'mav-tutorial',
        title: 'Opening Results in MAV-Aero',
        mdUrl: mdOpeningResultsInMAVTutorial
      },
      { id: 'mav-reference', title: 'MAV Integration Reference', mdUrl: mdMAVIntegrationReference }
    ]
  },
  {
    id: 'managing-account-tutorial',
    title: 'Managing Your Account',
    mdUrl: mdManagingYourAccountTutorial
  }
]

/** Maps a markdown filename to a doc id so `[Workflows](Workflows.md)` links resolve. */
const LINK_MAP: Record<string, string> = {
  'Overview.md': 'overview',
  'tutorials/GettingStarted.md': 'getting-started',
  'reference/Configuration.md': 'configuration',
  'reference/ShortcutsAndHiddenFeatures.md': 'shortcuts-hidden-features',
  'tutorials/Submissions.md': 'submissions-tutorial',
  'reference/Submissions.md': 'submissions-reference',
  'tutorials/ReadingResults.md': 'results-tutorial',
  'tutorials/PlottingResults.md': 'plotting-results-tutorial',
  'tutorials/ReviewingImages.md': 'reviewing-images-tutorial',
  'tutorials/RunMediaAndReports.md': 'run-media-reports-tutorial',
  'reference/Dashboard.md': 'dashboard-reference',
  'reference/ComplianceReporting.md': 'compliance-reporting-reference',
  'reference/RunMediaAndReports.md': 'run-media-reports-reference',
  'tutorials/OpeningResultsInMAV.md': 'mav-tutorial',
  'reference/MAVIntegration.md': 'mav-reference',
  'tutorials/ManagingYourAccount.md': 'managing-account-tutorial'
}

/**
 * Maps a markdown image path to its bundled asset URL. Every file in
 * `client/src/platform/images/` is registered automatically, so a page can use
 * `![alt](images/filename.png)` without touching this file.
 */
const IMAGE_MAP: Record<string, string> = {}
{
  // webpack-only API; typings for it vary by @types package, hence the cast.
  const ctx = (require as any).context('../platform/images', false, /\.(png|jpe?g|gif|webp|svg)$/)
  ctx.keys().forEach((key: string) => {
    IMAGE_MAP[`images/${key.replace(/^\.\//, '')}`] = ctx(key)
  })
}

const DEFAULT_DOC = 'overview'

const URL_MAP: Record<string, string> = {}
const ALL_DOCS: { id: string; title: string; mdUrl: string }[] = []
;(function walk(nodes: DocNode[]) {
  for (const node of nodes) {
    const { id, title, mdUrl } = node
    if (mdUrl) {
      URL_MAP[id] = mdUrl
      ALL_DOCS.push({ id, title, mdUrl })
    }
    if (node.children) walk(node.children)
  }
})(DOC_TREE)

const ChevronIcon: FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`h-3 w-3 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

const PlatformDocs: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedDoc = searchParams.get('doc')
  const selectedDoc = requestedDoc && URL_MAP[requestedDoc] ? requestedDoc : DEFAULT_DOC

  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set(DOC_TREE.map(n => n.id)))
  const [searchIndex, setSearchIndex] = useState<Record<string, string> | null>(null)

  const contentRef = useRef<HTMLDivElement>(null)
  const cache = useRef<Record<string, string>>({})

  const fetchDoc = useCallback(async (docId: string): Promise<string> => {
    if (cache.current[docId] !== undefined) return cache.current[docId]
    const res = await fetch(URL_MAP[docId])
    if (!res.ok) throw new Error(`Failed to load ${docId}`)
    const text = await res.text()
    cache.current[docId] = text
    return text
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchDoc(selectedDoc)
      .then(text => {
        if (!cancelled) setContent(text)
      })
      .catch(() => {
        if (!cancelled) setContent('# Unable to load\n\nThis document could not be loaded.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedDoc, fetchDoc])

  useEffect(() => {
    contentRef.current?.scrollTo(0, 0)
  }, [content])

  // Full-text search needs every document, so build the index on first search only.
  useEffect(() => {
    if (!query.trim() || searchIndex) return
    let cancelled = false
    Promise.all(
      ALL_DOCS.map(async doc => [doc.id, await fetchDoc(doc.id).catch(() => '')] as const)
    ).then(entries => {
      if (!cancelled) setSearchIndex(Object.fromEntries(entries))
    })
    return () => {
      cancelled = true
    }
  }, [query, searchIndex, fetchDoc])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return null
    return ALL_DOCS.flatMap(doc => {
      const body = searchIndex?.[doc.id] ?? ''
      const titleHit = doc.title.toLowerCase().includes(q)
      const bodyAt = body.toLowerCase().indexOf(q)
      if (!titleHit && bodyAt < 0) return []
      const snippet =
        bodyAt >= 0 ? `…${body.slice(Math.max(0, bodyAt - 60), bodyAt + 90).trim()}…` : ''
      return [{ id: doc.id, title: doc.title, snippet }]
    })
  }, [query, searchIndex])

  const selectDoc = useCallback(
    (docId: string) => {
      setSearchParams(docId === DEFAULT_DOC ? {} : { doc: docId })
    },
    [setSearchParams]
  )

  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const mdComponents = useMemo(
    () => ({
      h1: (p: any) => (
        <h1 className="mb-3 border-b border-gray-300 pb-2 text-3xl font-bold dark:border-gray-700">
          {p.children}
        </h1>
      ),
      h2: (p: any) => <h2 className="mb-2 mt-8 text-2xl font-semibold">{p.children}</h2>,
      h3: (p: any) => <h3 className="mb-2 mt-6 text-lg font-semibold">{p.children}</h3>,
      p: (p: any) => <p className="my-3 leading-relaxed">{p.children}</p>,
      ul: (p: any) => <ul className="my-3 list-disc space-y-1 pl-6">{p.children}</ul>,
      ol: (p: any) => <ol className="my-3 list-decimal space-y-1 pl-6">{p.children}</ol>,
      blockquote: (p: any) => (
        <blockquote className="my-4 border-l-4 border-blue-500 bg-blue-50 py-2 pl-4 dark:bg-slate-800">
          {p.children}
        </blockquote>
      ),
      code: (p: any) =>
        p.className ? (
          <code className={p.className}>{p.children}</code>
        ) : (
          <code className="rounded bg-gray-200 px-1.5 py-0.5 text-sm dark:bg-slate-700">
            {p.children}
          </code>
        ),
      pre: (p: any) => (
        <pre className="my-4 overflow-x-auto rounded bg-gray-900 p-4 text-sm text-gray-100">
          {p.children}
        </pre>
      ),
      table: (p: any) => (
        <div className="my-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">{p.children}</table>
        </div>
      ),
      th: (p: any) => (
        <th className="border border-gray-300 bg-gray-100 px-3 py-2 text-left font-semibold dark:border-gray-700 dark:bg-slate-800">
          {p.children}
        </th>
      ),
      td: (p: any) => (
        <td className="border border-gray-300 px-3 py-2 dark:border-gray-700">{p.children}</td>
      ),
      img: (p: any) => (
        <img
          className="my-4 max-w-full rounded shadow"
          src={IMAGE_MAP[p.src] ?? p.src}
          alt={p.alt}
        />
      ),
      a: (p: any) => {
        const href: string = p.href ?? ''
        // Full relative path first (disambiguates same-named files across subfolders), then bare filename.
        const docId = LINK_MAP[href] ?? LINK_MAP[href.split('/').pop() ?? '']
        if (docId) {
          return (
            <button
              type="button"
              className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400"
              onClick={() => selectDoc(docId)}>
              {p.children}
            </button>
          )
        }
        const external = /^https?:/.test(href)
        return (
          <a
            href={href}
            className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400"
            target={external ? '_blank' : undefined}
            rel={external ? 'noreferrer' : undefined}>
            {p.children}
          </a>
        )
      }
    }),
    [selectDoc]
  )

  const renderTree = (nodes: DocNode[], depth = 0) =>
    nodes.map(node => {
      const isOpen = expanded.has(node.id)
      return (
        <li key={node.id}>
          <div className="flex items-center gap-1" style={{ paddingLeft: depth * 12 }}>
            {node.children ? (
              <button
                type="button"
                aria-label={isOpen ? `Collapse ${node.title}` : `Expand ${node.title}`}
                className="p-1 text-gray-500 hover:text-gray-900 dark:hover:text-white"
                onClick={() => toggleExpanded(node.id)}>
                <ChevronIcon expanded={isOpen} />
              </button>
            ) : (
              <span className="w-5" />
            )}
            <button
              type="button"
              className={`flex-1 rounded px-2 py-1 text-left text-sm hover:bg-gray-200 dark:hover:bg-slate-700 ${
                node.children
                  ? 'font-semibold text-gray-900 dark:text-white'
                  : selectedDoc === node.id
                  ? 'bg-gray-200 font-semibold dark:bg-slate-700'
                  : 'text-gray-700 dark:text-gray-300'
              }`}
              onClick={() => (node.children ? toggleExpanded(node.id) : selectDoc(node.id))}>
              {node.title}
            </button>
          </div>
          {node.children && isOpen && <ul>{renderTree(node.children, depth + 1)}</ul>}
        </li>
      )
    })

  return (
    <div className="flex h-full min-h-0 gap-4 p-4">
      <aside className="flex w-64 shrink-0 flex-col overflow-hidden rounded border border-gray-300 bg-white dark:border-gray-700 dark:bg-slate-900">
        <div className="border-b border-gray-300 p-3 dark:border-gray-700">
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search documentation"
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-slate-800"
          />
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {results ? (
            results.length ? (
              <ul className="space-y-2">
                {results.map(r => (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="w-full rounded px-2 py-1 text-left hover:bg-gray-200 dark:hover:bg-slate-700"
                      onClick={() => selectDoc(r.id)}>
                      <span className="block text-sm font-semibold">{r.title}</span>
                      {r.snippet && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {r.snippet}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-2 text-sm text-gray-500">
                {searchIndex ? 'No matches found.' : 'Searching…'}
              </p>
            )
          ) : (
            <ul>{renderTree(DOC_TREE)}</ul>
          )}
        </nav>
      </aside>

      <main
        ref={contentRef}
        className="min-w-0 flex-1 overflow-y-auto rounded border border-gray-300 bg-white p-8 dark:border-gray-700 dark:bg-slate-900">
        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSlug]}
            components={mdComponents}>
            {content}
          </ReactMarkdown>
        )}
      </main>
    </div>
  )
}

export default PlatformDocs
